import type { SyntheticEvent } from "react"
import { Box, Skeleton, Tab, Tabs, Typography } from "@mui/material"
import { makeStyles } from "tss-react/mui"

import { LocalizedEnum } from "../../../../../localization/components/AppLocalized"
import type { ResourceType } from "../../../../admin/organization/resources/resourcesTypes"

import { BOOKING_TABS } from "./helpers"
import type { BookingTabCounts } from "./helpers"

const useStyles = makeStyles()((theme) => ({
    tabBar: {
        borderBottom: `.125rem solid ${theme.palette.tailwind.slate[100]}`,
    },
    tabs: {
        minHeight: "auto",
        marginBottom: "-.125rem",
    },
    indicator: {
        height: ".125rem",
        backgroundColor: theme.palette.tailwind.slate[900],
    },
    tab: {
        minWidth: "auto",
        minHeight: "auto",
        padding: ".75rem 1.5rem",
        textTransform: "none",
        color: theme.palette.tailwind.slate[400],
        "&.Mui-selected": {
            color: theme.palette.tailwind.slate[900],
        },
    },
    label: {
        display: "inline-flex",
        alignItems: "center",
        gap: ".5rem",
        fontWeight: theme.typography.fontWeightBold,
    },
    count: {
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        minWidth: "1.75rem",
        height: "1.5rem",
        padding: "0 .5rem",
        borderRadius: "6.25rem",
        fontWeight: theme.typography.fontWeightBold,
        backgroundColor: theme.palette.tailwind.slate[100],
        color: theme.palette.tailwind.slate[500],
    },
    countSelected: {
        backgroundColor: theme.palette.tailwind.slate[900],
        color: theme.palette.common.white,
    },
    countSkeleton: {
        width: "1.75rem",
        height: "1.5rem",
        borderRadius: "6.25rem",
    },
}))

type Props = Readonly<{
    value: ResourceType
    counts: BookingTabCounts
    isLoading: boolean
    onChange: (value: ResourceType) => void
}>

export function BookingResourceTabs({ value, counts, isLoading, onChange }: Props) {
    const { classes, cx } = useStyles()

    return (
        <Box className={classes.tabBar}>
            <Tabs
                value={value}
                onChange={(_event: SyntheticEvent, next: ResourceType) => onChange(next)}
                variant="scrollable"
                scrollButtons={false}
                className={classes.tabs}
                classes={{ indicator: classes.indicator }}
            >
                {BOOKING_TABS.map((tab) => (
                    <Tab
                        key={tab}
                        value={tab}
                        disableRipple
                        className={classes.tab}
                        label={
                            <span className={classes.label}>
                                <LocalizedEnum base="service-center-reports-dashboard-bookings-tab" enumValue={tab}>
                                    <Typography variant="subtitle1" component="span" className={classes.label}>
                                        {tab}
                                    </Typography>
                                </LocalizedEnum>
                                {isLoading ? (
                                    <Skeleton variant="rounded" className={classes.countSkeleton} />
                                ) : (
                                    <Typography
                                        variant="body2"
                                        component="span"
                                        className={cx(classes.count, tab === value && classes.countSelected)}
                                    >
                                        {counts[tab]}
                                    </Typography>
                                )}
                            </span>
                        }
                    />
                ))}
            </Tabs>
        </Box>
    )
}
